/** L2 report builder: pure, testable transforms over existing dataset rows (no new backend). */

export type ReportDataset = 'alarms' | 'lab' | 'production';

export interface Col {
  key: string;
  label: string;
  numeric?: boolean;
}
export interface ReportSpec {
  label: string;
  tsField: string;
  columns: Col[];
}

export const REPORT_SPECS: Record<ReportDataset, ReportSpec> = {
  alarms: {
    label: '报警',
    tsField: 'ts',
    columns: [
      { key: 'ts', label: '时间' }, { key: 'level', label: '级别' }, { key: 'system', label: '系统' },
      { key: 'message', label: '内容' }, { key: 'acknowledged', label: '已确认' },
    ],
  },
  lab: {
    label: '化验',
    tsField: 'sampleAt',
    columns: [
      { key: 'sampleAt', label: '采样' }, { key: 'operatorName', label: '化验人' },
      { key: 'ph', label: 'pH', numeric: true }, { key: 'nh3n', label: '氨氮', numeric: true },
      { key: 'no2', label: '亚盐', numeric: true }, { key: 'bypass', label: '旁路' },
    ],
  },
  production: {
    label: '生产批次',
    tsField: 'stockedAt',
    columns: [
      { key: 'unit', label: '单元' }, { key: 'species', label: '品种' }, { key: 'status', label: '状态' },
      { key: 'count', label: '尾数', numeric: true }, { key: 'biomassKg', label: '生物量kg', numeric: true },
      { key: 'feedKgTotal', label: '投喂kg', numeric: true }, { key: 'mortalityTotal', label: '损耗', numeric: true },
      { key: 'harvestTotalKg', label: '捕捞kg', numeric: true }, { key: 'survivalPct', label: '存活%', numeric: true },
      { key: 'fcePct', label: 'FCE%', numeric: true }, { key: 'feedCostYuan', label: '饲料成本元', numeric: true },
      { key: 'unitCostYuanPerKg', label: '单位成本元/kg', numeric: true },
      { key: 'nonFeedCostYuan', label: '运营分摊元', numeric: true }, { key: 'totalCostYuan', label: '完全成本元', numeric: true },
      { key: 'unitCostFullyLoadedYuanPerKg', label: '完全单位成本元', numeric: true },
    ],
  },
};

export interface ReportConfig {
  columns: string[];
  from?: string; // ISO date
  to?: string;
  groupBy?: string;
  aggCol?: string; // numeric column to sum when grouping
}
export interface ReportTable {
  headers: string[];
  rows: (string | number)[][];
}

/** Build a projected / time-filtered / grouped report table from raw dataset rows. */
export function buildReport(rows: Record<string, unknown>[], dataset: ReportDataset, cfg: ReportConfig): ReportTable {
  const spec = REPORT_SPECS[dataset];
  const from = cfg.from ? Date.parse(cfg.from) : -Infinity;
  const to = cfg.to ? Date.parse(cfg.to) + 86_399_999 : Infinity; // include the whole end day
  const filtered = rows.filter((r) => {
    const t = Date.parse(String(r[spec.tsField] ?? ''));
    return Number.isFinite(t) ? t >= from && t <= to : true;
  });

  if (cfg.groupBy) {
    const gcol = spec.columns.find((c) => c.key === cfg.groupBy);
    if (gcol) {
      const agg = cfg.aggCol ? spec.columns.find((c) => c.key === cfg.aggCol && c.numeric) : undefined;
      const groups = new Map<string, { n: number; sum: number }>();
      for (const r of filtered) {
        const key = String(r[gcol.key] ?? '—');
        const g = groups.get(key) ?? { n: 0, sum: 0 };
        g.n += 1;
        if (agg) g.sum += Number(r[agg.key]) || 0;
        groups.set(key, g);
      }
      const headers = [gcol.label, '计数', ...(agg ? [`${agg.label} 合计`] : [])];
      const out = [...groups.entries()]
        .sort((a, b) => b[1].n - a[1].n)
        .map(([k, g]) => [k, g.n, ...(agg ? [Math.round(g.sum * 100) / 100] : [])] as (string | number)[]);
      return { headers, rows: out };
    }
  }

  const cols = (cfg.columns.length ? cfg.columns : spec.columns.map((c) => c.key))
    .map((k) => spec.columns.find((c) => c.key === k))
    .filter((c): c is Col => !!c);
  const out = filtered.map((r) => cols.map((c) => {
    const v = r[c.key];
    if (typeof v === 'boolean') return v ? '是' : '否';
    return v == null ? '' : (v as string | number);
  }) as (string | number)[]);
  return { headers: cols.map((c) => c.label), rows: out };
}
